/**
 * Shared service-booking input helpers: the 30-minute time-slot window, the
 * time-unit duration stepper's label/ceiling, and the time-unit noun table.
 * One home for logic previously duplicated between `ServiceDetailSelector`
 * and `MobileServiceBooking` (standards B.7).
 */
import { guestsUpToHelper, guestsVehicleHelper } from '../constants/serviceSheetCopy';
import { ModifierGroup, Service, ServiceChildSummary } from '../types';
import { toIsoDateLocal } from './formatters';
import { hasPerGuestModifier } from './modifierSelection';
import {
  MAX_VEHICLE_UNITS,
  TimeUnitStepperConfig,
  isPerPersonUnit,
  timeUnitPluralNoun,
  vehicleUnitCount,
} from './pricingUnits';

/**
 * Builds the 30-minute time-slot options for a service booking from 06:00
 * through 22:30 inclusive. When `dateIso` is today (relative to `now`),
 * slots at or before the current time are excluded.
 *
 * "Today" is `now`'s LOCAL calendar day (`toIsoDateLocal`), never
 * `now.toISOString()`'s UTC one — a UTC-based comparison reads the wrong
 * day for any guest west of UTC after local evening rolls into the next
 * UTC day (e.g. 19:30 Mountain is already the next UTC day), which both
 * wrongly treats the real local today as an unfiltered future date AND
 * filters tomorrow's slots by the current clock time.
 */
export function buildServiceTimeSlots(dateIso: string, now: Date): string[] {
  const slots: string[] = [];
  const currentDate = toIsoDateLocal(now);
  const isToday = dateIso === currentDate;
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  for (let hour = 6; hour <= 22; hour++) {
    for (const minute of ['00', '30']) {
      if (isToday) {
        const slotMinute = parseInt(minute, 10);
        if (hour < currentHour || (hour === currentHour && slotMinute <= currentMinute)) {
          continue;
        }
      }
      slots.push(`${hour.toString().padStart(2, '0')}:${minute}`);
    }
  }
  return slots;
}

/** Guest-facing label for the duration/count stepper, keyed by the
 *  normalised pricing_unit. Falls back to a generic label for a time unit
 *  this table doesn't (yet) name — defensive only, since isTimeUnit already
 *  gates rendering to the three keys below. */
const TIME_UNIT_STEPPER_LABELS: Record<string, string> = {
  hour: 'Hours',
  '30min': 'Half-hours',
  day: 'Days',
};

/** The duration stepper's label for a resolved stepper config: "Hours" /
 *  "Half-hours" / "Days", or "Estimated hours" (etc.) for a metered option,
 *  whose final count is settled after the service. */
export function timeUnitStepperLabel(stepper: TimeUnitStepperConfig): string {
  return stepper.isMetered
    ? `Estimated ${timeUnitPluralNoun(stepper.pricingUnit) ?? 'units'}`
    : (TIME_UNIT_STEPPER_LABELS[stepper.pricingUnit] ?? 'Units');
}

/** Singular/plural noun for a time-unit service's "N <unit>" copy. */
const TIME_UNIT_NOUNS: Record<string, { singular: string; plural: string }> = {
  hour: { singular: 'hour', plural: 'hours' },
  '30min': { singular: 'half-hour', plural: 'half-hours' },
  day: { singular: 'day', plural: 'days' },
};

export function timeUnitNoun(unit: string | null | undefined, count: number): string {
  const normalised = (unit ?? '').trim().toLowerCase();
  const noun = TIME_UNIT_NOUNS[normalised];
  if (!noun) return unit || '';
  return count === 1 ? noun.singular : noun.plural;
}

/** Weekday-based availability check for a service booking date. When
 *  `availableDays` is absent or empty, every weekday is available. Shared
 *  by `ServiceDetailSelector` (desktop) and `MobileServiceBooking`'s Date
 *  step so the eligibility rule has one definition (standards B.7). */
export function isServiceDateAvailable(
  availableDays: string[] | null | undefined,
  dateIso: string,
): boolean {
  if (!availableDays || availableDays.length === 0) {
    return true;
  }
  const checkedDate = new Date(dateIso + 'T00:00:00');
  const dayName = checkedDate.toLocaleDateString('en-US', { weekday: 'long' });
  return availableDays.includes(dayName);
}

/** Fallback party-size ceiling when a listing carries no `max_capacity` at
 *  all, applied by `resolveServiceGuestBounds`. */
export const DEFAULT_SERVICE_GUEST_CAPACITY = 20;

export interface ServiceGuestBoundsInput {
  guests: number;
  /** Seats per booking (per vehicle for a vehicle-priced service). */
  capacity: number | null | undefined;
  minCapacity: number | null | undefined;
  isVehicleUnit: boolean;
}

export interface CapacityHolder {
  max_capacity?: number | null;
}

/**
 * Guest capacity that governs the booking: the selected option's own value,
 * never the parent's, once an option is chosen; the service's own value for a
 * standalone service. Null means no guest limit.
 */
export function selectedOptionCapacity(
  service: CapacityHolder,
  selectedVariant: CapacityHolder | null | undefined,
): number | null {
  if (selectedVariant) return selectedVariant.max_capacity ?? null;
  return service.max_capacity ?? null;
}

export interface ServiceAsksGuestCountInput {
  service: Pick<Service, 'max_capacity' | 'pricing_unit' | 'is_by_request'>;
  selectedVariant:
    Pick<ServiceChildSummary, 'max_capacity' | 'pricing_unit' | 'is_by_request'> | null | undefined;
  modifierGroups: ModifierGroup[];
}

/**
 * True when the guest page should ask for a guest count: the count changes
 * the price or is capped.
 */
export function serviceAsksGuestCount({
  service,
  selectedVariant,
  modifierGroups,
}: ServiceAsksGuestCountInput): boolean {
  const pricingUnit = selectedVariant?.pricing_unit ?? service.pricing_unit;
  return (
    selectedOptionCapacity(service, selectedVariant) !== null ||
    isPerPersonUnit(pricingUnit) ||
    Boolean(service.is_by_request || selectedVariant?.is_by_request) ||
    hasPerGuestModifier(modifierGroups)
  );
}

export interface ServiceGuestBounds {
  min: number;
  max: number;
  helperText: string;
}

/**
 * Bounds and helper line for the Guests stepper, shared by the desktop
 * booking card and the mobile sheet. A vehicle-priced service allows up to
 * MAX_VEHICLE_UNITS vehicles, and its helper names the seats in the vehicles
 * the current party needs ("1 vehicle · up to 6 guests"), not the ceiling.
 */
export function resolveServiceGuestBounds({
  guests,
  capacity,
  minCapacity,
  isVehicleUnit,
}: ServiceGuestBoundsInput): ServiceGuestBounds {
  const min = minCapacity ?? 1;
  if (!capacity) {
    return {
      min,
      max: DEFAULT_SERVICE_GUEST_CAPACITY,
      helperText: '',
    };
  }
  if (!isVehicleUnit) {
    return { min, max: capacity, helperText: guestsUpToHelper(capacity) };
  }
  const vehicleCount = vehicleUnitCount(guests, capacity);
  return {
    min,
    max: capacity * MAX_VEHICLE_UNITS,
    helperText: guestsVehicleHelper(vehicleCount, vehicleCount * capacity),
  };
}

/** Character ceiling for the stepped sheet's special-requests note,
 *  matching the legacy `ServiceDetailSelector` textarea. */
export const SPECIAL_REQUESTS_MAX_LENGTH = 500;
